<script setup lang="ts">
/**
 * The shell's workspaces (I-3641): stays mounted while another page is open, so
 * tiles keep their documents, and renders only on `/tiles`. Tiles are one flat
 * list placed by `layoutTree`: moving a tile changes its rectangle, never its
 * place in the DOM, because a moved iframe reloads.
 */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import Columns2 from '@lucide/vue/dist/esm/icons/columns-2.mjs';
import {
  NxContextMenu,
  NxContextMenuItem,
  NxContextMenuLabel,
  NxContextMenuSeparator,
  NxContextSubmenu,
  NxEmptyState,
  NxOfficeSheetTabs,
  NxSpinner,
  NxSplitterResizeHandle,
  useNxConfirm,
  useNxPrompt,
  useNxToasts,
} from "@blpde/nx-ui";
import {
  connectTileFrameOf,
  useAccountConfig,
  type ConnectTileMessage,
} from "@blpde/connect";
import TilePane from "./TilePane.vue";
import { appForPath } from "@/integration";
import { mayReloadForLoadFailure } from "@/loadFailure";
import { rowOpenTiles } from "./tileTargets";
import {
  HOME_ROUTE as HOME,
  routeLabel,
  tabLabel as labelOf,
  tileCaption,
} from "./tileLabels";
import {
  browserTabUrl,
  claimWindowId,
  cleanupPatch,
  liveWindows,
  newWindowId,
  windowUrl,
} from "./tileWindows";
import { useClosedWindows } from "./useClosedWindows";
import { tileSplitReuses, useTileNavbarMode, useTilesEnabled } from "./tileNav";
import {
  dropAt,
  layoutTree,
  listTiles,
  MAX_TILES,
  type TileDrop,
  type TileLeaf,
  type TileRect,
} from "./tileTree";
import {
  storedTiles,
  tilesPatch,
  useTileWorkspaces,
  type StoredTiles,
  type TileWorkspace,
} from "./useTileWorkspaces";

const props = defineProps<{
  /** Where the tab bar goes: a row of the shell under the surface. */
  barTarget: HTMLElement | null;
}>();

const emit = defineEmits<{
  /** Switched off, and every tile agreed: the shell lets go of this and shows `page`. */
  released: [page: string];
}>();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const confirm = useNxConfirm();
const prompt = useNxPrompt();
const { showToast } = useNxToasts();
const store = useTileWorkspaces();
const { workspaces, groups, activeId } = store;
const shown = computed(() => route.path === "/tiles");

/** One tile, not maximized from several: the tab looks like a plain page. */
function isSingle(workspace: TileWorkspace): boolean {
  return listTiles(workspace.root).length === 1;
}
const activeSingle = computed(
  () => !!store.active.value && isSingle(store.active.value),
);

/** A workspace opens on first sight, so a reload does not start every tile. */
const opened = ref(new Set<string>());

// ---- stored arrangement (`shell.tiles`) ----
const shellConfig = useAccountConfig<{
  tiles?: StoredTiles & { restore?: boolean };
}>("shell");
const restoreStored = computed(
  () => shellConfig.effective.value?.tiles?.restore !== false,
);
/** This browser window's id (`tileWindows.ts`); its tabs are stored under it. */
const windowId = ref<string | null>(null);
void claimWindowId().then((id) => {
  windowId.value = id;
});
/**
 * Master has not answered the first read for long: a tab stands in, so that a broken
 * read does not leave nothing at all to work with.
 */
const standingIn = ref(false);
/** Before master answered, an empty workspace would hide or overwrite the stored ones. */
const ready = computed(
  () => !!windowId.value && (store.hostReady.value || standingIn.value),
);

// A first read that failed is asked again until it is answered: the stored tabs are
// only out of reach (a server restarting), and nothing else of this page has its data.
const READ_RETRY_MS = 3000;
const STAND_IN_AFTER_MS = 30_000;
let firstReadFailed = false;
let readRetry: ReturnType<typeof setTimeout> | undefined;
let standInTimer: ReturnType<typeof setTimeout> | undefined;
function askAgain(): void {
  clearTimeout(readRetry);
  if (shellConfig.loaded.value) {
    clearTimeout(standInTimer);
    return;
  }
  if (!shellConfig.error.value) return;
  if (!firstReadFailed) {
    firstReadFailed = true;
    standInTimer = setTimeout(() => {
      standingIn.value = true;
    }, STAND_IN_AFTER_MS);
  }
  readRetry = setTimeout(() => {
    void shellConfig.reload().finally(askAgain);
  }, READ_RETRY_MS);
}
watch([shellConfig.error, shellConfig.loaded], askAgain, { immediate: true });

let saved: StoredTiles = {};
let hydrated = false;
/** The config row these workspaces belong to; null until the first write creates it. */
let hydratedTarget: string | null = null;
const targetOf = () => {
  const target = shellConfig.target.value;
  return target ? JSON.stringify(target) : null;
};
/** Another row than the one loaded: not the first write creating this one. */
const isOtherRow = () => {
  const target = targetOf();
  return hydrated && !!hydratedTarget && !!target && hydratedTarget !== target;
};

/** Nothing the user did: a tab that only stands in until the stored ones are in. */
function isBlankTab(workspace: TileWorkspace): boolean {
  const tiles = listTiles(workspace.root);
  return !workspace.label && tiles.length === 1 && tiles[0].route === HOME;
}

function loadStored(): void {
  // The opener may have put something here while master had not answered yet.
  const added = hydrated ? [] : workspaces.value.filter((w) => !isBlankTab(w));
  // Master is back, and this page started without it: a fresh start has everything,
  // and there is nothing to lose yet.
  if (
    firstReadFailed &&
    !hydrated &&
    !added.length &&
    mayReloadForLoadFailure("connect.firstReadRetry")
  ) {
    window.location.reload();
    return;
  }
  const stored = shellConfig.effective.value?.tiles;
  store.reset();
  opened.value = new Set();
  const id = windowId.value!;
  const handover = stored?.windows?.[id]?.[0]?.handover;
  if (restoreStored.value || handover) store.hydrate(stored, id);
  saved = storedTiles(id, workspaces.value, activeId.value, groups.value);
  // Tabs taken over from the single bar are not stored under this window yet.
  const adopted =
    restoreStored.value && !stored?.windows?.[id] && workspaces.value.length
      ? tilesPatch({}, saved)
      : null;
  if (adopted) void shellConfig.patch({ tiles: stamped(adopted) });
  // A window busy loading answers late, and what goes here does not come back.
  void liveWindows(1000).then((open) => {
    const cleanup = cleanupPatch(shellConfig.effective.value?.tiles, id, open);
    if (cleanup) void shellConfig.patch({ tiles: cleanup });
  });
  hydrated = true;
  store.setHostReady(true);
  hydratedTarget = targetOf();
  for (const workspace of added) {
    const same = showingSamePage(workspace);
    if (!same) workspaces.value = [...workspaces.value, workspace];
    activeId.value = (same ?? workspace).id;
  }
}

/** A page the stored tabs already show gets no second tab. */
function showingSamePage(workspace: TileWorkspace): TileWorkspace | undefined {
  const onlyRoute = (w: TileWorkspace) => {
    const tiles = listTiles(w.root);
    return tiles.length === 1 ? tiles[0].route : null;
  };
  const route = onlyRoute(workspace);
  return route ? workspaces.value.find((w) => onlyRoute(w) === route) : undefined;
}

// First answer, and again when the profile or company switches: its config row
// has its own workspaces, and this one's must not land there. The client sets
// `target` and `loaded` before the answer's values: seen right away, so nothing
// is written meanwhile, and read once the answer is applied.
let loadPending = false;
watch(
  [shellConfig.loaded, targetOf, windowId],
  ([loaded]) => {
    if (!loaded || !windowId.value || loadPending) return;
    if (!hydrated || isOtherRow()) {
      loadPending = true;
      store.setHostReady(false);
      void Promise.resolve().then(() => {
        loadPending = false;
        loadStored();
      });
    } else hydratedTarget = targetOf();
  },
  { flush: "sync", immediate: true },
);

// The config client coalesces and flushes its own writes. `saved` is what master
// confirmed: a write that failed goes again, with the next change or on its own.
const RETRY_MS = 15_000;
let retry: ReturnType<typeof setTimeout> | undefined;
/** Moved to another window: gone from here, but not closed. */
const handedOver = new Set<string>();

function saveTiles(): void {
  clearTimeout(retry);
  if (!shellConfig.loaded.value || !hydrated || loadPending || isOtherRow())
    return;
  const next = restoreStored.value
    ? storedTiles(
        windowId.value!,
        workspaces.value,
        activeId.value,
        groups.value,
      )
    : {};
  const before: StoredTiles = {
    ...saved,
    workspaces: Object.fromEntries(
      Object.entries(saved.workspaces ?? {}).filter(
        ([id]) => !handedOver.has(id),
      ),
    ),
  };
  const patch = tilesPatch(before, next);
  if (!patch) return;
  void shellConfig
    .patch({ tiles: stamped(patch) })
    .catch(() => null)
    .then((written) => {
      if (written) saved = next;
      else retry = setTimeout(saveTiles, RETRY_MS);
    });
}
watch([workspaces, activeId, groups], saveTiles);

/** Every written window carries the time, so closed windows sort by it. */
function stamped(patch: Record<string, unknown>): Record<string, unknown> {
  const windows = patch.windows as Record<string, unknown[] | null> | undefined;
  if (!windows) return patch;
  const savedAt = new Date().toISOString();
  const withTime = Object.fromEntries(
    Object.entries(windows).map(([id, value]) => [
      id,
      value ? [{ ...(value[0] as object), savedAt }] : null,
    ]),
  );
  return { ...patch, windows: withTime };
}

// ---- other browser windows ----

/** Moves tabs to a new browser window, open as they are; a group goes along. */
async function moveToWindow(ids: readonly string[]): Promise<void> {
  // Opened right away: after the questions below a browser would block it.
  const target = window.open("about:blank", "_blank");
  if (!target) {
    showToast(t("connect.tiles.windowBlocked"), "error");
    return;
  }
  const leaving = workspaces.value.filter((w) => ids.includes(w.id));
  const tiles = leaving.flatMap((w) => listTiles(w.root).map((x) => x.id));
  if (!leaving.length || !(await mayClose(tiles))) {
    target.close();
    return;
  }
  const id = newWindowId();
  const taken = groups.value.filter((group) =>
    leaving.some((w) => w.groupId === group.id),
  );
  const entry = storedTiles(id, leaving, leaving[0].id, taken);
  entry.windows![id][0].handover = true;
  // The tabs leave this window only once the other one is stored.
  const written = shellConfig
    .patch({ tiles: stamped(entry as Record<string, unknown>) })
    .catch(() => null);
  void shellConfig.flush();
  if (!(await written)) {
    target.close();
    returnToTiles(tiles);
    showToast(t("connect.tiles.moveFailed"), "error");
    return;
  }
  // Not closed: the tabs live on in the other window.
  for (const workspace of leaving) handedOver.add(workspace.id);
  store.handOver(leaving.map((w) => w.id));
  if (!workspaces.value.length && shown.value) store.addWorkspace("", HOME);
  target.location.href = windowUrl(id);
}

const closedWindows = useClosedWindows();
const closed = closedWindows.closed;

// ---- the stored tabs are still on their way ----
/** Not there after this long: master does not answer. Better said than an empty page. */
const WAITING_MS = 3000;
const waiting = ref(false);
let waitingTimer: ReturnType<typeof setTimeout> | undefined;
watch(
  ready,
  (isReady) => {
    clearTimeout(waitingTimer);
    waiting.value = false;
    if (!isReady)
      waitingTimer = setTimeout(() => {
        waiting.value = true;
      }, WAITING_MS);
  },
  { immediate: true },
);

// There is always a tab.
watch(
  [shown, ready],
  () => {
    if (shown.value && ready.value && !workspaces.value.length)
      store.addWorkspace("", HOME);
  },
  { immediate: true },
);
// Sync: a reload of the stored tabs empties `opened` and may end on the same active
// tab, and a later look would see no change and leave it closed: tabs without a tile.
watch(
  [shown, activeId],
  () => {
    if (shown.value && activeId.value && !opened.value.has(activeId.value))
      opened.value = new Set([...opened.value, activeId.value]);
  },
  { immediate: true, flush: "sync" },
);

/** Fixed like the tiles: reordering the tabs must not move a layer and reload its frames. */
const layersInDomOrder = computed(() =>
  workspaces.value
    .filter((w) => opened.value.has(w.id))
    .sort((a, b) => (a.id < b.id ? -1 : 1)),
);

function tabIcon(workspace: TileWorkspace) {
  const tiles = listTiles(workspace.root);
  if (tiles.length > 1) return Columns2;
  return tiles[0].route ? appForPath(tiles[0].route)?.icon : undefined;
}

const tabs = computed(() =>
  workspaces.value.map((w) => ({
    id: w.id,
    label: labelOf(w, t),
    closable: !isLastHomeTab(w),
    icon: tabIcon(w),
    groupId: w.groupId,
  })),
);

/** There is always a tab: the only one, on the start page, has nothing left to close. */
function isLastHomeTab(workspace: TileWorkspace): boolean {
  if (workspaces.value.length !== 1 || workspace.label) return false;
  const tiles = listTiles(workspace.root);
  return tiles.length === 1 && tiles[0].route === HOME;
}

function showTab(id: string): void {
  activeId.value = id;
  if (!shown.value) void router.push("/tiles");
}

function addTab(): void {
  store.addWorkspace("", HOME);
  if (!shown.value) void router.push("/tiles");
}

function layoutOf(workspace: TileWorkspace) {
  return layoutTree(workspace.root);
}

/** A fixed DOM order: a keyed list that reorders moves its nodes, and that reloads an iframe. */
function tilesInDomOrder(workspace: TileWorkspace) {
  return [...layoutOf(workspace).tiles].sort((a, b) =>
    a.tile.id < b.tile.id ? -1 : 1,
  );
}

const pct = (share: number) => `${share * 100}%`;
function rectStyle(rect: TileRect) {
  return {
    left: pct(rect.x),
    top: pct(rect.y),
    width: pct(rect.width),
    height: pct(rect.height),
  };
}
function tileStyle(workspace: TileWorkspace, tileId: string, rect: TileRect) {
  if (!workspace.maximizedTileId) return rectStyle(rect);
  return workspace.maximizedTileId === tileId
    ? rectStyle({ x: 0, y: 0, width: 1, height: 1 })
    : { visibility: "hidden" as const };
}

// ---- the navbar setting reaches the tiles without a reload ----
const navbarMode = useTileNavbarMode();
watch(navbarMode, (mode) => {
  for (const workspace of workspaces.value) {
    for (const tile of listTiles(workspace.root)) {
      store.sendToTile(tile.id, { type: "connect.tile.navbar", mode });
    }
  }
});

// ---- which tiles a record list can open into ----
function tileLabel(tile: TileLeaf): string {
  const name =
    (tile.route && routeLabel(tile.route, t)) || t("connect.tiles.empty");
  const caption = tileCaption(tile);
  return caption ? `${name} · ${caption}` : name;
}

function targetsOf(workspace: TileWorkspace) {
  return listTiles(workspace.root).map((tile) => ({
    id: tile.id,
    label: tileLabel(tile),
  }));
}

function sendTargets(tileId: string): void {
  const workspace = workspaces.value.find((w) =>
    listTiles(w.root).some((x) => x.id === tileId),
  );
  if (!workspace) return;
  store.sendToTile(tileId, {
    type: "connect.tile.targets",
    tiles: targetsOf(workspace).filter((target) => target.id !== tileId),
  });
}

watch(
  () => JSON.stringify(workspaces.value.map(targetsOf)),
  () => {
    for (const workspace of workspaces.value)
      listTiles(workspace.root).forEach((x) => sendTargets(x.id));
  },
);
watch(
  () => (store.active.value ? targetsOf(store.active.value) : []),
  (targets) => {
    rowOpenTiles.value = targets;
  },
  { immediate: true },
);

// ---- messages from the tiles ----
const panes = new Map<string, InstanceType<typeof TilePane>>();
function setPane(tileId: string, pane: unknown): void {
  if (pane) panes.set(tileId, pane as InstanceType<typeof TilePane>);
  else panes.delete(tileId);
}

function onMessage(event: MessageEvent<ConnectTileMessage>): void {
  if (event.origin !== location.origin || event.data?.type !== "connect.tile")
    return;
  const { tileId, path, title, area, nav, open } = event.data;
  const pane = panes.get(tileId);
  if (pane?.frameWindow() !== event.source) return;
  if (event.data.pressed) store.pressTile(tileId);
  if (event.data.failed) pane.frameFailed();
  else if (event.data.booted) pane.frameBooted();
  // Only a page it reached: its menu or caption may come without one.
  else if (path !== undefined) pane.frameShown();
  if (path !== undefined || title !== undefined || area !== undefined)
    store.reportTile(tileId, { path, title, area });
  if (nav) store.setTileNav(tileId, nav);
  // A frame that just started has not heard the current setting yet.
  if (nav)
    store.sendToTile(tileId, {
      type: "connect.tile.navbar",
      mode: navbarMode.value,
    });
  if (nav) sendTargets(tileId);
  if (open?.target === "tile" && open.tileId)
    store.openInTile(open.tileId, open.path);
  if (open?.target === "split")
    store.openFromTile(tileId, open.path, tileSplitReuses());
  if (open?.target === "new-split")
    store.openFromTile(tileId, open.path, false);
  if (open?.target === "workspace") store.addWorkspace("", open.path);
}

onMounted(() => {
  store.setLeaveGuard({
    leave: () =>
      mayClose(
        workspaces.value.flatMap((w) => listTiles(w.root).map((x) => x.id)),
      ),
    stay: () =>
      returnToTiles(
        workspaces.value.flatMap((w) => listTiles(w.root).map((x) => x.id)),
      ),
  });
  store.setGaveUpRestart((tileId) =>
    panes.get(tileId)?.frameGaveUp() ? () => void reloadTiles([tileId]) : null,
  );
  window.addEventListener("message", onMessage);
  if (area.value && typeof ResizeObserver !== "undefined") {
    areaObserver = new ResizeObserver(([entry]) =>
      store.setAreaSize(entry.contentRect),
    );
    areaObserver.observe(area.value);
  }
});
onBeforeUnmount(() => {
  areaObserver?.disconnect();
  store.setLeaveGuard(null);
  store.setGaveUpRestart(null);
  clearTimeout(retry);
  clearTimeout(waitingTimer);
  clearTimeout(readRetry);
  clearTimeout(standInTimer);
  // Switched off or signed out: when this comes back it reads the stored tabs anew.
  store.reset();
  store.setHostReady(false);
  window.removeEventListener("message", onMessage);
  endDrag();
});

// ---- closing asks the tile ----
function frameOf(tileId: string) {
  return connectTileFrameOf(panes.get(tileId)?.frameWindow());
}

/** False: the user keeps the tile, because of unsaved work or an app's own guard. */
async function mayClose(tileIds: readonly string[]): Promise<boolean> {
  const unsaved = tileIds.some((id) => frameOf(id)?.hasUnsavedWork());
  if (
    unsaved &&
    !(await confirm({
      title: t("connect.tiles.closeUnsavedTitle"),
      message: t("connect.tiles.closeUnsaved"),
      tone: "danger",
    }))
  ) {
    return false;
  }
  const left: string[] = [];
  const wasActive = activeId.value;
  let kept = true;
  for (const id of tileIds) {
    const frame = frameOf(id);
    if (!frame) continue;
    // A guard's question appears in the tile, so the tile has to be in view.
    await bringIntoView(id);
    if (!(await frame.leave())) {
      returnToTiles(left);
      kept = false;
      break;
    }
    left.push(id);
  }
  if (kept && wasActive && workspaces.value.some((w) => w.id === wasActive))
    activeId.value = wasActive;
  return kept;
}

async function bringIntoView(tileId: string): Promise<void> {
  const workspace = workspaces.value.find((w) =>
    listTiles(w.root).some((tile) => tile.id === tileId),
  );
  if (!workspace) return;
  activeId.value = workspace.id;
  store.setActiveTile(workspace.id, tileId);
  if (!shown.value) await router.push("/tiles");
  await nextTick();
}

/** Tiles that already left for closing, when it does not happen after all. */
function returnToTiles(tileIds: readonly string[]): void {
  for (const workspace of workspaces.value) {
    for (const tile of listTiles(workspace.root)) {
      if (tile.route && tileIds.includes(tile.id))
        store.sendToTile(tile.id, {
          type: "connect.tile.navigate",
          path: tile.route,
        });
    }
  }
}

/** A tile stuck loading gets a fresh start; unsaved work is asked for first. */
async function reloadTiles(tileIds: readonly string[]): Promise<void> {
  const unsaved = tileIds.some((id) => frameOf(id)?.hasUnsavedWork());
  if (
    unsaved &&
    !(await confirm({
      title: t("connect.tiles.closeUnsavedTitle"),
      message: t("connect.tiles.reloadUnsaved"),
      tone: "danger",
    }))
  )
    return;
  for (const id of tileIds) panes.get(id)?.reloadFrame();
}

async function closeTile(
  workspace: TileWorkspace,
  tileId: string,
): Promise<void> {
  if (await mayClose([tileId])) store.closeTile(workspace.id, tileId);
}

async function closeWorkspace(id: string): Promise<void> {
  const workspace = workspaces.value.find((w) => w.id === id);
  if (
    !workspace ||
    !(await mayClose(listTiles(workspace.root).map((tile) => tile.id)))
  )
    return;
  store.closeWorkspace(id);
  if (!workspaces.value.length && shown.value) store.addWorkspace("", HOME);
}

async function closeGroup(id: string): Promise<void> {
  const members = workspaces.value.filter((w) => w.groupId === id);
  for (const workspace of members) await closeWorkspace(workspace.id);
}

// ---- tab menu ----
const menu = ref<{ x: number; y: number; id: string } | null>(null);
const menuOpen = computed({
  get: () => !!menu.value,
  set: (open) => {
    if (!open) menu.value = null;
  },
});
const renamingId = ref<string | null>(null);
const menuWorkspace = computed(() =>
  workspaces.value.find((w) => w.id === menu.value?.id),
);
/** Only a tab with one thing in it has one address for a browser tab. */
const menuRoute = computed(() => {
  const tiles = menuWorkspace.value ? listTiles(menuWorkspace.value.root) : [];
  return tiles.length === 1 ? tiles[0].route : null;
});

function openInBrowserTab(path: string): void {
  window.open(browserTabUrl(path), "_blank", "noopener");
}

function tabCanSplit(side: "right" | "bottom"): boolean {
  const workspace = menuWorkspace.value;
  return (
    !!workspace && store.canSplit(workspace.id, workspace.activeTileId, side)
  );
}

function splitTab(workspaceId: string, side: "right" | "bottom"): void {
  const workspace = workspaces.value.find((w) => w.id === workspaceId);
  if (!workspace) return;
  store.splitTile(workspace.id, workspace.activeTileId, side);
  showTab(workspace.id);
}

async function newGroup(workspaceId: string): Promise<void> {
  const label = await prompt({ title: t("connect.tiles.newGroup") });
  if (label) store.createGroup(workspaceId, label);
}

const addMenu = ref<{ x: number; y: number } | null>(null);
const addMenuOpen = computed({
  get: () => !!addMenu.value,
  set: (open) => {
    if (!open) addMenu.value = null;
  },
});
async function openAddMenu(event: MouseEvent): Promise<void> {
  const at = { x: event.clientX, y: event.clientY };
  await closedWindows.load();
  addMenu.value = at;
}

const groupMenu = ref<{ x: number; y: number; id: string } | null>(null);
const groupMenuOpen = computed({
  get: () => !!groupMenu.value,
  set: (open) => {
    if (!open) groupMenu.value = null;
  },
});

async function renameGroup(id: string): Promise<void> {
  const value = groups.value.find((group) => group.id === id)?.label;
  const label = await prompt({ title: t("connect.tiles.renameGroup"), value });
  if (label) store.renameGroup(id, label);
}

// ---- switched off in the settings (`shell.tiles.enabled`) ----
const tilesEnabled = useTilesEnabled();
watch(
  tilesEnabled,
  async (enabled) => {
    if (enabled) return;
    const all = workspaces.value.flatMap((w) =>
      listTiles(w.root).map((tile) => tile.id),
    );
    // What is in front goes on as the one page of this browser tab.
    const front = store.active.value;
    const frontTile =
      front &&
      listTiles(front.root).find((tile) => tile.id === front.activeTileId);
    const page = shown.value ? frontTile?.route || HOME : route.fullPath;
    if (!(await mayClose(all)))
      void shellConfig.patch({ tiles: { enabled: true } as never });
    // Switched on again while the tiles were asked: they stay.
    else if (tilesEnabled.value) returnToTiles(all);
    else emit("released", page);
  },
  { immediate: true },
);

// ---- dividers ----
const area = ref<HTMLElement | null>(null);
let areaObserver: ResizeObserver | undefined;
const resizing = ref(false);

function moveDivider(
  workspace: TileWorkspace,
  splitId: string,
  delta: number,
): void {
  const box = area.value?.getBoundingClientRect();
  const divider = layoutOf(workspace).dividers.find(
    (d) => d.split.id === splitId,
  );
  if (!box || !divider || !delta) return;
  const size =
    divider.split.direction === "row"
      ? divider.rect.width * box.width
      : divider.rect.height * box.height;
  if (size > 0)
    store.resizeSplit(
      workspace.id,
      splitId,
      divider.split.ratio + delta / size,
    );
}

function dividerStyle(
  rect: TileRect,
  direction: "row" | "column",
  ratio: number,
) {
  return direction === "row"
    ? {
        left: pct(rect.x + rect.width * ratio),
        top: pct(rect.y),
        width: "0",
        height: pct(rect.height),
      }
    : {
        left: pct(rect.x),
        top: pct(rect.y + rect.height * ratio),
        width: pct(rect.width),
        height: "0",
      };
}

// ---- moving a tile ----
const DRAG_THRESHOLD_PX = 6;
const drag = ref<{
  tileId: string;
  targetId: string | null;
  drop: TileDrop | null;
} | null>(null);
let dragStart: { x: number; y: number; tileId: string } | null = null;

function onHeadDown(tileId: string, event: PointerEvent): void {
  if (event.button !== 0) return;
  dragStart = { x: event.clientX, y: event.clientY, tileId };
  window.addEventListener("pointermove", onDragMove);
  window.addEventListener("pointerup", onDragEnd);
  window.addEventListener("pointercancel", endDrag);
  window.addEventListener("keydown", onDragKey);
}

function onDragMove(event: PointerEvent): void {
  if (!dragStart) return;
  if (!drag.value) {
    if (
      Math.hypot(event.clientX - dragStart.x, event.clientY - dragStart.y) <
      DRAG_THRESHOLD_PX
    )
      return;
    drag.value = { tileId: dragStart.tileId, targetId: null, drop: null };
  }
  const under = document
    .elementFromPoint(event.clientX, event.clientY)
    ?.closest<HTMLElement>("[data-tile-id]");
  const targetId = under?.dataset.tileId ?? null;
  if (!under || !targetId || targetId === drag.value.tileId) {
    drag.value = { ...drag.value, targetId: null, drop: null };
    return;
  }
  const box = under.getBoundingClientRect();
  drag.value = {
    ...drag.value,
    targetId,
    drop: dropAt(
      (event.clientX - box.left) / box.width,
      (event.clientY - box.top) / box.height,
    ),
  };
}

function onDragEnd(): void {
  const done = drag.value;
  if (done?.targetId && done.drop && activeId.value) {
    store.dropTile(activeId.value, done.tileId, done.targetId, done.drop);
  }
  endDrag();
}

function onDragKey(event: KeyboardEvent): void {
  if (event.key === "Escape") endDrag();
}

function endDrag(): void {
  dragStart = null;
  drag.value = null;
  window.removeEventListener("pointermove", onDragMove);
  window.removeEventListener("pointerup", onDragEnd);
  window.removeEventListener("pointercancel", endDrag);
  window.removeEventListener("keydown", onDragKey);
}

const framesInert = computed(() => !!drag.value || resizing.value);
</script>

<template>
  <div v-show="shown" class="cx-workspaces" data-testid="tile-workspaces">
    <div
      ref="area"
      class="cx-workspaces-area"
      :class="{ 'is-single': activeSingle }"
    >
      <div v-if="waiting" class="cx-workspaces-waiting">
        <NxSpinner />
        <NxEmptyState
          compact
          :title="t('connect.tiles.waitingTitle')"
          :description="t('connect.tiles.waiting')"
        />
      </div>
      <div
        v-for="workspace in layersInDomOrder"
        v-show="workspace.id === activeId"
        :key="workspace.id"
        class="cx-workspaces-layer"
      >
        <div
          v-for="{ tile, rect } in tilesInDomOrder(workspace)"
          :key="tile.id"
          class="cx-workspaces-slot"
          :style="tileStyle(workspace, tile.id, rect)"
        >
          <TilePane
            :ref="(pane) => setPane(tile.id, pane)"
            :tile="tile"
            :active="workspace.activeTileId === tile.id"
            :maximized="workspace.maximizedTileId === tile.id"
            :frame-inert="framesInert"
            :moving="!!drag"
            :lifted="drag?.tileId === tile.id"
            :drop-target="drag?.targetId === tile.id ? drag.drop : null"
            :single="isSingle(workspace)"
            :can-split-right="store.canSplit(workspace.id, tile.id, 'right')"
            :can-split-down="store.canSplit(workspace.id, tile.id, 'bottom')"
            @activate="store.setActiveTile(workspace.id, tile.id)"
            @pick="store.setTileRoute(workspace.id, tile.id, $event)"
            @split="store.splitTile(workspace.id, tile.id, $event)"
            @home="store.openInTile(tile.id, HOME)"
            @reload="reloadTiles([tile.id])"
            @running="store.setTileRunning(tile.id, $event)"
            @maximize="store.toggleMaximized(workspace.id, tile.id)"
            @close="closeTile(workspace, tile.id)"
            @drag-start="onHeadDown(tile.id, $event)"
          />
        </div>
        <template v-if="!workspace.maximizedTileId">
          <div
            v-for="{ split, rect } in layoutOf(workspace).dividers"
            :key="split.id"
            class="cx-workspaces-divider"
            :style="dividerStyle(rect, split.direction, split.ratio)"
          >
            <NxSplitterResizeHandle
              :orientation="
                split.direction === 'row' ? 'vertical' : 'horizontal'
              "
              :position="0"
              :min="-100000"
              :max="100000"
              :label="t('connect.tiles.resize')"
              @update:position="moveDivider(workspace, split.id, $event)"
              @drag-start="resizing = true"
              @drag-end="resizing = false"
            />
          </div>
        </template>
      </div>
    </div>
    <Teleport v-if="props.barTarget" :to="props.barTarget">
      <NxOfficeSheetTabs
        v-model:renaming-id="renamingId"
        class="cx-workspaces-bar"
        compact
        :tabs="tabs"
        :groups="groups"
        :selected-id="shown ? activeId : null"
        :aria-label="t('connect.tiles.workspaces')"
        :add-label="t('connect.tiles.addWorkspace')"
        :close-label="t('connect.tiles.closeWorkspace')"
        :scroll-back-label="t('connect.tiles.scrollBack')"
        :scroll-forward-label="t('connect.tiles.scrollForward')"
        reorderable
        renamable
        @select="showTab"
        @add="addTab"
        @close="closeWorkspace"
        @move="store.moveWorkspace"
        @rename="store.renameWorkspace"
        @toggle-group="store.toggleGroup"
        @move-to-group="store.addToGroup"
        @menu="
          (event, id) => (menu = { x: event.clientX, y: event.clientY, id })
        "
        @group-menu="
          (event, id) =>
            (groupMenu = { x: event.clientX, y: event.clientY, id })
        "
        @add-menu="openAddMenu"
      />
      <NxContextMenu
        v-model:open="addMenuOpen"
        :x="addMenu?.x ?? 0"
        :y="addMenu?.y ?? 0"
      >
        <NxContextMenuItem @select="addTab()">
          {{ t("connect.tiles.addWorkspace") }}
        </NxContextMenuItem>
        <NxContextMenuSeparator />
        <NxContextMenuLabel>{{
          t("connect.tiles.closedWindows")
        }}</NxContextMenuLabel>
        <NxContextMenuItem
          v-for="entry in closed"
          :key="entry.id"
          @select="closedWindows.reopen(entry.id)"
        >
          {{ closedWindows.label(entry) }}
        </NxContextMenuItem>
        <NxContextMenuItem v-if="!closed.length" disabled>
          {{ t("connect.tiles.noClosedWindows") }}
        </NxContextMenuItem>
        <template v-else>
          <NxContextMenuSeparator />
          <NxContextMenuItem @select="closedWindows.forgetAll()">
            {{ t("connect.tiles.closeAllClosed") }}
          </NxContextMenuItem>
        </template>
      </NxContextMenu>
      <NxContextMenu
        v-model:open="menuOpen"
        :x="menu?.x ?? 0"
        :y="menu?.y ?? 0"
      >
        <NxContextMenuItem @select="menu && newGroup(menu.id)">
          {{ t("connect.tiles.newGroup") }}
        </NxContextMenuItem>
        <NxContextSubmenu
          v-if="groups.length"
          :label="t('connect.tiles.addToGroup')"
        >
          <NxContextMenuItem
            v-for="group in groups"
            :key="group.id"
            @select="menu && store.addToGroup(menu.id, group.id)"
          >
            {{ group.label }}
          </NxContextMenuItem>
        </NxContextSubmenu>
        <NxContextMenuItem
          v-if="menuWorkspace?.groupId"
          @select="menu && store.removeFromGroup(menu.id)"
        >
          {{ t("connect.tiles.removeFromGroup") }}
        </NxContextMenuItem>
        <NxContextMenuSeparator />
        <NxContextMenuItem
          :disabled="!tabCanSplit('right')"
          :description="
            tabCanSplit('right')
              ? undefined
              : t('connect.tiles.splitFull', { max: MAX_TILES })
          "
          @select="menu && splitTab(menu.id, 'right')"
        >
          {{ t("connect.tiles.splitRight") }}
        </NxContextMenuItem>
        <NxContextMenuItem
          :disabled="!tabCanSplit('bottom')"
          :description="
            tabCanSplit('bottom')
              ? undefined
              : t('connect.tiles.splitFull', { max: MAX_TILES })
          "
          @select="menu && splitTab(menu.id, 'bottom')"
        >
          {{ t("connect.tiles.splitDown") }}
        </NxContextMenuItem>
        <NxContextMenuSeparator />
        <NxContextMenuItem
          v-if="menuWorkspace"
          @select="
            menuWorkspace &&
            reloadTiles(listTiles(menuWorkspace.root).map((x) => x.id))
          "
        >
          {{ t("connect.tiles.reload") }}
        </NxContextMenuItem>
        <NxContextMenuItem @select="renamingId = menu?.id ?? null">
          {{ t("connect.tiles.renameWorkspace") }}
        </NxContextMenuItem>
        <NxContextMenuItem
          v-if="menuRoute"
          @select="menuRoute && openInBrowserTab(menuRoute)"
        >
          {{ t("connect.tiles.openInBrowserTab") }}
        </NxContextMenuItem>
        <NxContextMenuItem @select="menu && moveToWindow([menu.id])">
          {{ t("connect.tiles.moveToWindow") }}
        </NxContextMenuItem>
        <NxContextMenuSeparator />
        <NxContextMenuItem
          v-if="menuWorkspace && !isLastHomeTab(menuWorkspace)"
          @select="menu && closeWorkspace(menu.id)"
        >
          {{ t("connect.tiles.closeWorkspace") }}
        </NxContextMenuItem>
      </NxContextMenu>
      <NxContextMenu
        v-model:open="groupMenuOpen"
        :x="groupMenu?.x ?? 0"
        :y="groupMenu?.y ?? 0"
      >
        <NxContextMenuItem @select="groupMenu && renameGroup(groupMenu.id)">
          {{ t("connect.tiles.renameGroup") }}
        </NxContextMenuItem>
        <NxContextMenuItem
          @select="groupMenu && store.dissolveGroup(groupMenu.id)"
        >
          {{ t("connect.tiles.dissolveGroup") }}
        </NxContextMenuItem>
        <NxContextMenuItem
          @select="
            groupMenu &&
            moveToWindow(
              workspaces
                .filter((w) => w.groupId === groupMenu!.id)
                .map((w) => w.id),
            )
          "
        >
          {{ t("connect.tiles.moveGroupToWindow") }}
        </NxContextMenuItem>
        <NxContextMenuSeparator />
        <NxContextMenuItem @select="groupMenu && closeGroup(groupMenu.id)">
          {{ t("connect.tiles.closeGroup") }}
        </NxContextMenuItem>
      </NxContextMenu>
    </Teleport>
  </div>
</template>

<style scoped>
.cx-workspaces {
  display: flex;
  flex-direction: column;
  background: var(--nxt-neutral-subtle-bg);
}
.cx-workspaces-area {
  position: relative;
  flex: 1;
  min-height: 0;
  margin: var(--nxt-space-2xs);
}
.cx-workspaces-area.is-single {
  margin: 0;
}
.cx-workspaces-waiting {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--nxt-space-md);
}
.cx-workspaces-area.is-single .cx-workspaces-slot {
  padding: 0;
}
.cx-workspaces-layer {
  position: absolute;
  inset: 0;
}
.cx-workspaces-slot {
  position: absolute;
  padding: calc(var(--nxt-space-2xs) / 2);
}
.cx-workspaces-divider {
  position: absolute;
  z-index: 1;
}
.cx-workspaces-bar {
  flex: 1;
}
</style>
